import React, { useEffect, useState, useRef } from "react";
import intlTelInput from "intl-tel-input";
import Select from "react-select";
import ReactCountryFlag from "react-country-flag";
import { getCode, getNames } from "country-list";
import "intl-tel-input/build/css/intlTelInput.css";
import "intl-tel-input/build/js/utils";

// Build country list
const countryOptions = getNames().map((name) => ({
    label: name,
    value: name,
    code: getCode(name),
}));

countryOptions.sort((a, b) =>
    a.label === "Kenya" ? -1 : b.label === "Kenya" ? 1 : a.label.localeCompare(b.label)
);

export default function BookingModal({ pkg, onClose }) {
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [country, setCountry] = useState("");
    const [travelFrom, setTravelFrom] = useState("");
    const [travelTo, setTravelTo] = useState("");
    const [adults, setAdults] = useState(1);
    const [children, setChildren] = useState(0);
    const [childrenAges, setChildrenAges] = useState("");
    const [accommodation, setAccommodation] = useState("No Preference");
    const [notes, setNotes] = useState("");
    const [consent, setConsent] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [status, setStatus] = useState("");

    const phoneInputRef = useRef(null);
    const itiInstanceRef = useRef(null);

    // Auto-detect country from IP
    useEffect(() => {
        fetch("https://ipapi.co/json/")
            .then((res) => res.json())
            .then((data) => {
                if (data.country_name) setCountry(data.country_name);
            })
            .catch(() => console.log("Country detection failed"));
    }, []);

    useEffect(() => {
        setTimeout(() => {
            if (phoneInputRef.current) {
                itiInstanceRef.current = intlTelInput(phoneInputRef.current, {
                    initialCountry: "ke",
                    utilsScript:
                        "https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/17.0.8/js/utils.js",
                });
            }
        }, 100);
    }, []);

    const handleBookingSubmit = async (e) => {
        e.preventDefault();
        if (!pkg) return;

        const phone = itiInstanceRef.current ? itiInstanceRef.current.getNumber() : "";

        setSubmitting(true);
        setStatus("");

        try {
            const res = await fetch(process.env.REACT_APP_API_URL_EMAIL + "/send-package-enquiry", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    packageId: pkg.id,
                    title: pkg.title,
                    destination: pkg.destination,
                    duration: pkg.duration,
                    description: pkg.description,
                    image: pkg.image,
                    name,
                    email,
                    phone,
                    country,
                    travelFrom,
                    travelTo,
                    adults,
                    children,
                    childrenAges,
                    accommodation,
                    notes,
                    consent,
                }),
            });

            const result = await res.json();
            if (!res.ok) throw new Error(result.message || "Failed to send booking");
            setStatus("✅ Booking request sent successfully!");
        } catch (err) {
            setStatus(`❌ ${err.message}`);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="modal show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
            <div className="modal-dialog modal-dialog-scrollable">
                <div className="modal-content">
                    <div className="modal-header navyblue-bg">
                        <h5 className="modal-title text-white">Book: {pkg.title}</h5>
                        <button className="btn-close icon-color" onClick={onClose} />
                    </div>
                    <div className="modal-body" style={{ maxHeight: "80vh", overflowY: "auto" }}>
                        <form onSubmit={handleBookingSubmit}>
                            <div className="mb-3">
                                <label className="form-label">Full Name*</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    required
                                />
                            </div>

                            <div className="mb-3">
                                <label className="form-label">Email Address*</label>
                                <input
                                    type="email"
                                    className="form-control"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                />
                            </div>

                            <div className="mb-3 intl-input-wrapper">
                                <label className="form-label">Phone / WhatsApp Number*</label>
                                <input type="tel" className="form-control" ref={phoneInputRef} required />
                            </div>

                            <div className="mb-3">
                                <label className="form-label">Country of Residence*</label>
                                <Select
                                    options={countryOptions}
                                    value={countryOptions.find((c) => c.value === country) || null}
                                    onChange={(selected) => setCountry(selected.value)}
                                    placeholder="Select your country"
                                    isSearchable
                                    formatOptionLabel={(option) => (
                                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                            <ReactCountryFlag countryCode={option.code} svg style={{ width: "1.5em", height: "1.5em" }} />
                                            {option.label}
                                        </div>
                                    )}
                                    className="mb-2"
                                />
                            </div>

                            <div className="mb-3 d-flex gap-2">
                                <div className="flex-fill">
                                    <label className="form-label">Arrival Date*</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={travelFrom}
                                        onChange={(e) => setTravelFrom(e.target.value)}
                                        required
                                    />
                                </div>
                                <div className="flex-fill">
                                    <label className="form-label">Departure Date*</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={travelTo}
                                        onChange={(e) => setTravelTo(e.target.value)}
                                        required
                                    />
                                </div>
                            </div>

                            <div className="mb-3 d-flex gap-2">
                                <div className="flex-fill">
                                    <label className="form-label">Adults*</label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        min="1"
                                        value={adults}
                                        onChange={(e) => setAdults(e.target.value)}
                                        required
                                    />
                                </div>
                                <div className="flex-fill">
                                    <label className="form-label">Children</label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        min="0"
                                        value={children}
                                        onChange={(e) => setChildren(e.target.value)}
                                    />
                                </div>
                            </div>

                            {children > 0 && (
                                <div className="mb-3">
                                    <label className="form-label">Children Ages (comma separated)</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={childrenAges}
                                        onChange={(e) => setChildrenAges(e.target.value)}
                                        placeholder="e.g., 5,8"
                                    />
                                </div>
                            )}

                            <div className="mb-3">
                                <label className="form-label">Accommodation Preference</label>
                                <select
                                    className="form-select"
                                    value={accommodation}
                                    onChange={(e) => setAccommodation(e.target.value)}
                                >
                                    <option value="No Preference">No Preference</option>
                                    <option value="Luxury">Luxury</option>
                                    <option value="Mid-Range">Mid-Range</option>
                                    <option value="Budget">Budget</option>
                                </select>
                            </div>

                            <div className="mb-3">
                                <label className="form-label">Special Requests / Notes</label>
                                <textarea
                                    className="form-control"
                                    rows="3"
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    placeholder="Dietary needs, special celebrations, accessibility, etc."
                                />
                            </div>

                            <div className="mb-3 form-check">
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    checked={consent}
                                    onChange={(e) => setConsent(e.target.checked)}
                                    required
                                />
                                <label className="form-check-label">
                                    I agree to be contacted by Primedale Tours & Travel regarding my inquiry.
                                </label>
                            </div>

                            <button className="btn navyblue-bg" disabled={submitting}>
                                {submitting ? "Submitting..." : "Submit Booking"}
                            </button>
                            <button type="button" className="btn btn-secondary ms-2" onClick={onClose}>
                                Cancel
                            </button>

                            {status && <div className="alert alert-info mt-3">{status}</div>}
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
}
